import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Container, Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getBankDropdownData,
  getCompanyLoanMasters,
  getEmpTypesList,
  getParentEmpTypes,
  getPartnerLoanMasters,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import CustomSelect from '../../components/CustomSelect';
import CustomInput from '../../components/CustomInput';
import { fDate2 } from '../../utils/formatTime';
import { PolicyApi } from '../../utils/Apis';
import { yesNoOptions } from '../../utils/CommonFunction';

// const policyValidationSchema = yup.object().shape({
//   bankId: yup
//     .mixed()
//     .required('Please select bank name')
//     .notOneOf(['', ' '], 'Please select bank name'),

//   aggrementPolicyName: yup
//     .string()
//     .trim()
//     .required('Please select policy name')
//     .notOneOf(['', ' '], 'Please select valid policy name'),

//   loanTypeId: yup
//     .mixed()
//     .required('Please select loan type')
//     .notOneOf(['', ' '], 'Please select valid loan type'),

//   loanSubTypeId: yup
//     .mixed()
//     .required('Please select loan sub type')
//     .notOneOf(['', ' '], 'Please select valid loan sub type'),

//   ageMin: yup
//     .number()
//     .typeError('Please enter minimum age')
//     .required('Please enter minimum age')
//     .min(18, 'Minimum age must be at least 18')
//     .max(100, 'Minimum age cannot be more than 100'),

//   ageMax: yup
//     .number()
//     .typeError('Please enter maximum age')
//     .required('Please enter maximum age')
//     .min(18, 'Maximum age must be at least 18')
//     .max(100, 'Maximum age cannot be more than 100')
//     .test('greater-than-min', 'Maximum age must be greater than minimum age', function (value) {
//       const { ageMin } = this.parent;
//       return typeof value === 'number' && typeof ageMin === 'number' ? value > ageMin : true;
//     }),

//   employmentTypeId: yup
//     .mixed()
//     .required('Please select employment type')
//     .notOneOf(['', ' '], 'Please select employment type'),

//   employmentSubTypeId: yup
//     .mixed()
//     .required('Please select employment sub type')
//     .notOneOf(['', ' '], 'Please select employment sub type'),

//   minCibilScore: yup
//     .number()
//     .typeError('Please enter cibil score')
//     .required('Please enter cibil score'),

//   collateralRequest: yup
//     .mixed()
//     .required('Please select collateral request')
//     .notOneOf(['', ' '], 'Please select collateral request'),

//   interestRateMin: yup
//     .number()
//     .typeError('Please enter minimum interest rate')
//     .required('Please enter minimum interest rate'),

//   interestRateMax: yup
//     .number()
//     .typeError('Please enter maximum interest rate')
//     .required('Please enter maximum interest rate')
//     .test('greater-than-min', 'Please enter valid maximum interest rate', function (value) {
//       const { interestRateMin } = this.parent;
//       return value >= interestRateMin;
//     }),

//   preProcessingCharge: yup
//     .number()
//     .typeError('Please enter pre processing charge')
//     .required('Please enter pre processing charge'),

//   downPayment: yup
//     .number()
//     .typeError('Please enter down payment')
//     .required('Please enter down payment'),

//   loanToValueRatio: yup
//     .number()
//     .typeError('Please enter loan to value ratio')
//     .required('Please enter loan to value ratio')
//     .max(100, 'Please enter valid loan to value ratio'),

//   policyStartDate: yup
//     .date()
//     .typeError('Please enter start date')
//     .required('Please enter start date'),

//   policyEndDate: yup
//     .date()
//     .typeError('Please enter end date')
//     .required('Please enter end date')
//     .test('is-after-start', 'Please enter valid end date', function (value) {
//       const { policyStartDate } = this.parent;
//       return policyStartDate && value >= policyStartDate;
//     })
// });

function AddPolicy(parent) {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageType, rowData } = location?.state || {};

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const {
    BankDrop,
    ParentLoanMasterListDrop,
    EmpTypesListDrop,
    LoanSubTypeListDrop,
    ParentEmpTypesListDrop
  } = useGlobalSelector();

  const {
    handleSubmit: handleReduxSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange', // Add this to validate on blur
    defaultValues: {
      bankId: '',
      aggrementPolicyName: '',
      loanTypeId: '',
      loanSubTypeId: '',
      ageMin: '',
      ageMax: '',
      employmentTypeId: '',
      employmentSubTypeId: '',
      minCibilScore: '',
      collateralRequest: '',
      interestRateMin: '',
      interestRateMax: '',
      preProcessingCharge: '',
      downPayment: '',
      loanToValueRatio: '',
      policyStartDate: '',
      policyEndDate: ''
    }
  });
  const watchEmploymentTypeId = watch('employmentTypeId');
  const watchLoanTypeId = watch('loanTypeId');

  useEffect(() => {
    if (pageType === 'Edit') {
      getCompanyLoanMastersApi(rowData?.loanTypeId);
      getEmpTypesListApi(rowData?.employmentTypeId);
      setValue('bankId', rowData?.bank?.bankId);
      setValue('aggrementPolicyName', rowData?.aggrementPolicyName);
      setValue('loanTypeId', rowData?.loanTypeId);
      setValue('loanSubTypeId', rowData?.loanSubTypeId);
      setValue('ageMin', rowData?.ageMin);
      setValue('ageMax', rowData?.ageMax);
      setValue('employmentTypeId', rowData?.employmentTypeId);
      setValue('employmentSubTypeId', rowData?.employmentSubTypeId);
      setValue('minCibilScore', rowData?.minCibilScore);
      setValue('collateralRequest', rowData?.collateralRequest);
      setValue('interestRateMin', rowData?.interestRateMin);
      setValue('interestRateMax', rowData?.interestRateMax);
      setValue('preProcessingCharge', rowData?.preProcessingCharge);
      setValue('downPayment', rowData?.downPayment);
      setValue('loanToValueRatio', rowData?.loanToValueRatio);
      setValue('policyStartDate', rowData?.policyStartDate);
      setValue('policyEndDate', rowData?.policyEndDate);
    }
  }, [pageType, rowData]);

  // submit api
  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        ...data,
        aggrementPolicyCode: pageType === 'Edit' ? rowData?.aggrementPolicyCode : null,
        aggrementPolicyDesc: null,
        aggrementPolicyId: pageType === 'Edit' ? rowData?.aggrementPolicyId : null,
        policyEndDate: data?.policyEndDate ? fDate2(data?.policyEndDate) : null,
        policyStartDate: data?.policyStartDate ? fDate2(data?.policyStartDate) : null
      };
      try {
        const res = await dispatch(saveApiData(PolicyApi?.addPolicy, finalData));
        if (res?.status === 'success') {
          navigate('/policy');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, rowData, pageType] // 🔹 depends on LoginData
  );

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  const getPartnerLoanMastersApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId };
    dispatch(getPartnerLoanMasters(data));
  }, [LoginData]);

  const getCompanyLoanMastersApi = useCallback(
    (parentId) => {
      if (!LoginData || !parentId) return;
      const data = {
        companyId: LoginData?.companyId,
        parentLoanId: parentId
      };
      dispatch(getCompanyLoanMasters(data));
    },
    [LoginData]
  );

  const getEmpTypesListApi = useCallback(
    (categoryId) => {
      if (!LoginData || !categoryId) return;
      const data = {
        companyId: LoginData?.companyId,
        employmentCategoryId: categoryId
      };
      // dispatch(getEmpTypesList(data));
    },
    [LoginData]
  );

  const getParentEmpTypesApi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId
    };
    dispatch(getParentEmpTypes(data));
  }, [LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  useEffect(() => {
    getPartnerLoanMastersApi();
  }, [getPartnerLoanMastersApi]);

  useEffect(() => {
    getParentEmpTypesApi();
  }, [getParentEmpTypesApi]);

  return (
    <Page title="Partner Management System || Agreement">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info={pageType === 'Edit' ? 'Edit Policy' : 'Add Policy'} />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Policy Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <form id="policySubmitForm" onSubmit={handleReduxSubmit(onSubmit)}>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={4}>
                  <CustomSelect
                    name="bankId"
                    label="Bank Name"
                    control={control}
                    errors={errors}
                    options={BankDrop || []}
                    isRequired
                    fieldCode="NAME"
                    fieldType="ALPHABET"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="aggrementPolicyName"
                    label="Policy Name"
                    type="textarea"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="NAME"
                    fieldType="ALPHABET"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="loanTypeId"
                    label="Loan Type"
                    control={control}
                    errors={errors}
                    options={ParentLoanMasterListDrop || []}
                    isRequired
                    onChange={(event, val, option, reason, details) => {
                      setValue('loanSubTypeId', null);
                      getCompanyLoanMastersApi(val);
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="loanSubTypeId"
                    label="Loan Sub Type"
                    disabled={!watchLoanTypeId}
                    control={control}
                    errors={errors}
                    options={LoanSubTypeListDrop || []}
                    isRequired
                  />{' '}
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="ageMin"
                    label="Age (Min)"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="AGE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="ageMax"
                    label="Age (Max)"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="AGE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="employmentTypeId"
                    label="Employment Type"
                    control={control}
                    errors={errors}
                    options={ParentEmpTypesListDrop || []}
                    isRequired
                    onChange={(event, val, option, reason, details) => {
                      setValue('employmentSubTypeId', null);
                      getEmpTypesListApi(val);
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomSelect
                    name="employmentSubTypeId"
                    label="Employment Sub Type"
                    control={control}
                    disabled={!watchEmploymentTypeId}
                    errors={errors}
                    options={EmpTypesListDrop || []}
                    isRequired
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="minCibilScore"
                    label="Cibil Score(Min)"
                    type="textarea"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="CIBIL"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>{' '}
                <Grid item xs={4}>
                  <CustomSelect
                    name="collateralRequest"
                    label="Collateral Request"
                    control={control}
                    errors={errors}
                    options={yesNoOptions || []}
                    isRequired
                    fieldCode="INTEREST_RATE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="interestRateMin"
                    label="Interest Rate (Min)"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="INTEREST_RATE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="interestRateMax"
                    label="Interest Rate (Max)"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="INTEREST_RATE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="preProcessingCharge"
                    label="Pre Processing Charge"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="PERCENTAGE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="downPayment"
                    label="Down Payment"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="PERCENTAGE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="loanToValueRatio"
                    label="Loan to Value Ratio"
                    type="Number"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="PERCENTAGE"
                    fieldType="NUMBER_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="policyStartDate"
                    label="Start Date"
                    type="date"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
                <Grid item xs={4}>
                  <CustomInput
                    name="policyEndDate"
                    label="End Date"
                    type="date"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldCode="DATE"
                    fieldType="DATE_FIELD"
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
          <Box className="" textAlign="right">
            <Button variant="contained" type="submit" form="policySubmitForm" className="mainBtn">
              Save
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}

export default AddPolicy;
